import { addDoc, collection, doc, increment, serverTimestamp, setDoc } from 'firebase/firestore';
import { ensureAuth, getFirebaseServices } from '../firebase';
import { parseClientMetadata } from '../utils/clientMetadata';
import { createPageSnapshot, createSessionPathTracker } from './sessionTracking';

const PROFILE_COLLECTION = 'visitor_profiles';
const EVENT_COLLECTION = 'visitor_events';

const VISITOR_ID_KEY = 'visitor_metrics.visitor_id';
const SESSION_ID_KEY = 'visitor_metrics.session_id';
const FIRST_SEEN_MS_KEY = 'visitor_metrics.first_seen_ms';
const SESSION_TRACKED_PREFIX = 'visitor_metrics.tracked';

function getStorage(type) {
  if (typeof window === 'undefined') return null;
  return window[type];
}

function createId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getOrCreateStoredId(storage, key) {
  if (!storage) return createId();
  const existing = storage.getItem(key);
  if (existing) return existing;
  const next = createId();
  storage.setItem(key, next);
  return next;
}

function getOrCreateFirstSeenMs(storage) {
  if (!storage) return Date.now();
  const existing = Number(storage.getItem(FIRST_SEEN_MS_KEY));
  if (Number.isFinite(existing) && existing > 0) return existing;
  const next = Date.now();
  storage.setItem(FIRST_SEEN_MS_KEY, String(next));
  return next;
}

function markTrackedThisSession(pathKey) {
  const sessionStorage = getStorage('sessionStorage');
  if (!sessionStorage) return;
  const key = `${SESSION_TRACKED_PREFIX}:${pathKey}`;
  sessionStorage.setItem(key, '1');
}

function isTrackedThisSession(pathKey) {
  const sessionStorage = getStorage('sessionStorage');
  if (!sessionStorage) return false;
  const key = `${SESSION_TRACKED_PREFIX}:${pathKey}`;
  return sessionStorage.getItem(key) === '1';
}

const trackPathOnce = createSessionPathTracker({
  isTracked: isTrackedThisSession,
  markTracked: markTrackedThisSession,
});

async function fetchJsonWithTimeout(url, timeoutMs = 3500) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

function normalizeGeoFromIpapi(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.error) return null;
  return {
    provider: 'ipapi.co',
    ip: typeof payload.ip === 'string' ? payload.ip : null,
    city: payload.city || null,
    region: payload.region || null,
    regionCode: payload.region_code || null,
    country: payload.country_name || null,
    countryCode: payload.country_code || null,
    postal: payload.postal || null,
    latitude: Number.isFinite(payload.latitude) ? payload.latitude : null,
    longitude: Number.isFinite(payload.longitude) ? payload.longitude : null,
    timezone: payload.timezone || null,
    isp: payload.org || null,
    asn: payload.asn || null,
  };
}

function normalizeGeoFromIpwho(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.success === false) return null;
  return {
    provider: 'ipwho.is',
    ip: typeof payload.ip === 'string' ? payload.ip : null,
    city: payload.city || null,
    region: payload.region || null,
    regionCode: payload.region_code || null,
    country: payload.country || null,
    countryCode: payload.country_code || null,
    postal: payload.postal || null,
    latitude: Number.isFinite(payload.latitude) ? payload.latitude : null,
    longitude: Number.isFinite(payload.longitude) ? payload.longitude : null,
    timezone: payload.timezone?.id || null,
    isp: payload.connection?.isp || null,
    asn: payload.connection?.asn || null,
  };
}

async function resolveIpGeo() {
  const ipapiPayload = await fetchJsonWithTimeout('https://ipapi.co/json/');
  const ipapiGeo = normalizeGeoFromIpapi(ipapiPayload);
  if (ipapiGeo) return ipapiGeo;

  const ipwhoPayload = await fetchJsonWithTimeout('https://ipwho.is/');
  const ipwhoGeo = normalizeGeoFromIpwho(ipwhoPayload);
  if (ipwhoGeo) return ipwhoGeo;

  return null;
}

function maskIp(ip) {
  if (!ip || typeof ip !== 'string') return null;
  if (ip.includes('.')) {
    const parts = ip.split('.');
    if (parts.length !== 4) return null;
    return `${parts[0]}.${parts[1]}.${parts[2]}.0`;
  }
  if (ip.includes(':')) {
    const parts = ip.split(':').filter(Boolean);
    if (parts.length === 0) return null;
    return `${parts.slice(0, 4).join(':')}::`;
  }
  return null;
}

function fallbackHash(value) {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) - hash) + value.charCodeAt(i);
    hash |= 0;
  }
  return `legacy-${Math.abs(hash)}`;
}

async function hashValue(value) {
  if (!value) return null;
  if (typeof crypto === 'undefined' || !crypto.subtle || typeof TextEncoder === 'undefined') {
    return fallbackHash(value);
  }
  try {
    const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    const bytes = Array.from(new Uint8Array(buffer));
    return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return fallbackHash(value);
  }
}

async function upsertVisitorProfile(profileRef, basePayload) {
  await setDoc(profileRef, {
    ...basePayload,
    lastSeenAt: serverTimestamp(),
    visitCount: increment(1),
  }, { merge: true });
}

export async function trackVisitorSession(pageSnapshot) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const firebase = getFirebaseServices();
  if (!firebase) return false;

  const snapshot = pageSnapshot || createPageSnapshot(window.location, document.title);
  const referrer = document.referrer || null;
  const { path: pagePath, url: pageUrl, title: pageTitle } = snapshot;

  return trackPathOnce(pagePath, async () => {
    const localStorage = getStorage('localStorage');
    const sessionStorage = getStorage('sessionStorage');
    const visitorId = getOrCreateStoredId(localStorage, VISITOR_ID_KEY);
    const sessionId = getOrCreateStoredId(sessionStorage, SESSION_ID_KEY);
    const firstSeenAtMs = getOrCreateFirstSeenMs(localStorage);

    const device = parseClientMetadata();
    const geo = await resolveIpGeo();
    const ipMasked = maskIp(geo?.ip || null);
    const ipHash = await hashValue(geo?.ip || null);
    const shouldStoreRawIp = process.env.REACT_APP_VISITOR_STORE_RAW_IP === 'true';
    const geoPayload = geo
      ? {
          ...geo,
          ip: shouldStoreRawIp ? geo.ip : null,
        }
      : null;

    await ensureAuth(firebase.auth);

    const profileRef = doc(firebase.db, PROFILE_COLLECTION, visitorId);
    const profilePayload = {
      visitorId,
      lastSessionId: sessionId,
      lastPath: pagePath,
      lastUrl: pageUrl,
      lastTitle: pageTitle,
      referrer,
      firstSeenAtMs,
      device,
      geo: geoPayload,
      ipMasked,
      ipHash,
    };

    await upsertVisitorProfile(profileRef, profilePayload);

    await addDoc(collection(firebase.db, EVENT_COLLECTION), {
      eventType: 'page_view',
      visitorId,
      sessionId,
      path: pagePath,
      url: pageUrl,
      title: pageTitle,
      referrer,
      device,
      geo: geoPayload,
      ipMasked,
      ipHash,
      visitedAt: serverTimestamp(),
    });
  });
}
